import type { Contract } from '@gmn-portal/backend';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BackendActivityItem, FindPayloadApiClientParams } from '@/core/api';
import { payloadClient } from '@/core/api';

export const contractKeys = {
  all: ['contracts'] as const,
  lists: () => [...contractKeys.all, 'list'] as const,
  list: (params: ContractQueryParams) => [...contractKeys.lists(), params] as const,
  details: () => [...contractKeys.all, 'detail'] as const,
  detail: (id: string) => [...contractKeys.details(), id] as const,
  versions: () => [...contractKeys.all, 'versions'] as const,
  version: (id: string) => [...contractKeys.versions(), id] as const,
} as const;

export type ContractQueryParams = FindPayloadApiClientParams<'contracts', Contract> & {
  disabled?: boolean;
  status?: 'open' | 'closed';
};

export interface ContractUpdateParams {
  id: string;
  data: Partial<Contract>;
}

export const useContracts = (params?: ContractQueryParams) => {
  return useQuery({
    queryKey: contractKeys.list(params ?? {}),
    enabled: !params?.disabled,
    placeholderData: (previousData) => previousData,
    queryFn: async () => {
      const { status, ...otherParams } = params || {};

      // Build where clause with status filter if provided
      const whereClause = {
        ...(otherParams.where ? otherParams.where : {}),
        ...(status
          ? {
              and: [...(otherParams.where?.and || []), { status: { equals: status } }],
            }
          : {}),
      };

      const response = await payloadClient.find({
        collection: 'contracts',
        ...otherParams,
        where: whereClause,
      });
      return response;
    },
  });
};

export const useContract = (id: string, params?: { disabled?: boolean; depth?: number }) => {
  return useQuery({
    queryKey: contractKeys.detail(id),
    enabled: !params?.disabled,
    queryFn: async () => {
      const response = await payloadClient.findById({
        collection: 'contracts',
        id,
        ...(params?.depth !== undefined && { depth: params.depth }),
      });
      return response;
    },
  });
};

export const useContractVersions = (
  id: string,
  params?: { disabled?: boolean; limit?: number; page?: number }
) => {
  interface ActivityFeedResponse {
    docs: BackendActivityItem[];
    totalDocs: number;
    limit: number;
    totalPages: number;
    page: number;
    pagingCounter: number;
    hasPrevPage: boolean;
    hasNextPage: boolean;
    prevPage: number | null;
    nextPage: number | null;
  }

  return useInfiniteQuery<ActivityFeedResponse>({
    queryKey: contractKeys.version(id),
    enabled: !params?.disabled,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.nextPage,
    queryFn: async ({ pageParam = 1 }) => {
      const response = await payloadClient.customRequest<ActivityFeedResponse>({
        subpath: `/contracts/${id}/activity_feed?limit=${params?.limit || 15}&page=${pageParam}`,
        method: 'GET',
      });
      return response;
    },
  });
};

export const useCreateContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<Contract>) => {
      const response = await payloadClient.create({
        collection: 'contracts',
        data,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractKeys.all });
    },
  });
};

export const useUpdateContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: ContractUpdateParams) => {
      const response = await payloadClient.updateById({
        collection: 'contracts',
        id,
        data,
      });
      return response;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: contractKeys.all });
      queryClient.invalidateQueries({ queryKey: contractKeys.detail(id) });
    },
  });
};

export const useDeleteContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await payloadClient.deleteById({
        collection: 'contracts',
        id,
      });
      return response;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: contractKeys.all });
      queryClient.invalidateQueries({ queryKey: contractKeys.detail(id) });
    },
  });
};

export const useCopyContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, newStartDate }: { id: string; newStartDate?: string }) => {
      const response = await payloadClient.customRequest<Contract>({
        subpath: `/contracts/${id}/copy`,
        method: 'POST',
        data: newStartDate ? { newStartDate } : undefined,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractKeys.all });
    },
  });
};
